import React, { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../utils/httpRequest";

/* The official 42courses blob mark, used for a collected stamp. */
const StampMark = () => (
  <svg className="stamp-mark" viewBox="0 0 200 200" aria-hidden="true" focusable="false">
    <path fill="#2E2437" d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z" />
    <path fill="#2E2437" d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z" />
    <path fill="#fff" d="M70.59,137.36H84.13v-9.25h7.62V116.46H84.13V78.51h-11l-26.3,41.12v8.48H70.59Zm-9-20.13,10.11-16.79v16.79Z" />
    <path fill="#fff" d="M115.8,124.85v-.17l9.16-9.51c7.8-8.13,9.51-13.19,9.51-19.87,0-11-8.91-17.56-19-17.56s-16.53,5.05-20,11.13L105.26,96c2.57-3.77,5.57-5.48,9.08-5.48a6.08,6.08,0,0,1,6.17,6.34c0,4.2-2.66,7.88-7.28,12.77l-17.4,18.76.18,9h39.06V124.85Z" />
  </svg>
);

/* Reward marker at the 5-stamp slot: a course on us. */
const CourseRewardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9 H21 V20 a1 1 0 0 1 -1 1 H4 a1 1 0 0 1 -1 -1 z" />
    <path d="M3 6 a1 1 0 0 1 1 -1 H20 a1 1 0 0 1 1 1 V9 H3 z" />
    <path d="M12 5 V21" />
    <path d="M8.5 5 C 7 2.5, 10 1, 12 4 C 14 1, 17 2.5, 15.5 5 H8.5 z" />
  </svg>
);

/* Reward marker at the 10-stamp slot: a full All Access Pass. */
const PassRewardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9 L7 13 L12 7 L17 13 L21 9 V18 a1 1 0 0 1 -1 1 H4 a1 1 0 0 1 -1 -1 z" />
    <circle cx="3" cy="9" r="1.2" fill="currentColor" />
    <circle cx="12" cy="7" r="1.2" fill="currentColor" />
    <circle cx="21" cy="9" r="1.2" fill="currentColor" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="9" height="10" rx="1.5" />
    <path d="M6 4 V3 a1 1 0 0 1 1 -1 h6 a1 1 0 0 1 1 1 v8 a1 1 0 0 1 -1 1 h-1" />
  </svg>
);

/* Positions (0-indexed) of the two reward slots on the stamp card:
   the 5th stamp (a course) and the 10th stamp (an All Access Pass). This
   mirrors the old badge behaviour, which marked these slots regardless of
   the configured threshold. */
const COURSE_REWARD_INDEX = 4;
const PASS_REWARD_INDEX = 9;

const shareMessage = (code: string) =>
  `Hi! I've been doing CPD-accredited Mini MBAs on 42courses, taught by the likes of Rory Sutherland and Dan Ariely. Behavioural science, marketing, leadership, AI. Thought you'd enjoy it. Use my code ${code} at checkout for 15% off any course or subscription.`;

const fallbackCopy = (text: string) => {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } catch (e) {
    /* no-op */
  }
  document.body.removeChild(ta);
};

const Referral = () => {
  const [refCode, setRefCode] = useState("");
  const [css, setCss] = useState<string[]>([]);
  const [threshold, setThreshold] = useState(0);
  const [achieved, setAchieved] = useState(0);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimeout = useRef<number | null>(null);

  useEffect(() => {
    getRequest("/api/referrals/defaults.json").then((response: AxiosResponse) => {
      setRefCode(response.data.referral_code || "");
      setCss(response.data.css || []);
      setThreshold(Number(response.data.threshold) || 0);
      setAchieved(Number(response.data.achieved) || 0);
    });

    return () => {
      if (copyTimeout.current) window.clearTimeout(copyTimeout.current);
    };
  }, []);

  const copy = (key: string, text: string) => {
    const done = () => {
      setCopiedKey(key);
      if (copyTimeout.current) window.clearTimeout(copyTimeout.current);
      copyTimeout.current = window.setTimeout(() => setCopiedKey(null), 1800);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done, () => {
        fallbackCopy(text);
        done();
      });
    } else {
      fallbackCopy(text);
      done();
    }
  };

  const message = shareMessage(refCode);
  const encoded = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/?text=${encoded}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent("Thought you'd like this")}&body=${encoded}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encoded}`;

  /* Stamp slots: driven by the server's referrals_result_classes ('won' =
     collected). Falls back to an empty ten-slot card until data loads. */
  const totalSlots = css.length || 10;
  const stamps = Array.from({ length: totalSlots }, (_, idx) => ({
    idx,
    filled: css[idx] === "won",
    isCourseReward: idx === COURSE_REWARD_INDEX,
    isPassReward: idx === PASS_REWARD_INDEX,
  }));

  const tier = threshold > 0 ? threshold : 5;
  const courseRemaining = Math.max(tier - achieved, 0);
  const passRemaining = Math.max(tier * 2 - achieved, 0);

  const renderStatus = () => {
    if (courseRemaining > 0) {
      return (
        <>
          <strong>{courseRemaining} more</strong> to a course on us.{" "}
          <strong>{passRemaining} more</strong> to a full All Access Pass.
        </>
      );
    }
    if (passRemaining > 0) {
      return (
        <>
          Course unlocked. <strong>{passRemaining} more</strong> to a full All Access Pass.
        </>
      );
    }
    return <>You've unlocked both rewards. Nicely done.</>;
  };

  return (
    <main className="referrals" id="main-content">
      {/* HERO */}
      <header className="hero">
        <div className="hero-bignum" aria-hidden="true">42</div>
        <div className="container">
          <div className="hero-eyebrow">Referral Programme</div>
          <h1 className="hero-title">Smart people know <em>smart people.</em></h1>
          <p className="hero-sub">Send a friend our way. They get 15% off any course. You collect stamps that turn into courses and All Access Passes.</p>
        </div>
      </header>

      <section className="referral-shell" aria-label="Your referral details">
        <div className="container">

          <div className="referral-grid">

            {/* LEFT: Sharing kit */}
            <div className="ref-card">
              <h2>Your sharing kit</h2>
              <p className="ref-card-sub">Send your code to anyone you think would like 42courses. They get 15% off at checkout. You collect a stamp on every purchase.</p>

              <div className="ref-card-eyebrow">Your code</div>
              <div className="code-row">
                <span className="code-value" id="refCode">{refCode}</span>
                <button
                  className={`code-copy-btn${copiedKey === "code" ? " copied" : ""}`}
                  type="button"
                  onClick={() => copy("code", refCode)}
                >
                  <CopyIcon />
                  <span className="btn-label">{copiedKey === "code" ? "Copied" : "Copy"}</span>
                </button>
              </div>

              <div className="or-divider" role="separator" aria-label="Or send via">Or send via</div>

              <div className="share-buttons">
                <a className="share-btn share-btn-whatsapp" href={whatsappUrl} target="_blank" rel="noopener">
                  <span className="share-btn-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.73 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01zM12.05 20.15h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.19-.31a8.22 8.22 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.83c-.01 4.54-3.71 8.23-8.25 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.37-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.05s.88 2.38 1.01 2.54c.12.17 1.74 2.66 4.22 3.73.59.25 1.05.4 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.18-.48-.3z" /></svg>
                  </span>
                  WhatsApp
                </a>
                <a className="share-btn share-btn-email" href={emailUrl}>
                  <span className="share-btn-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7 l9 6 l9 -6" /></svg>
                  </span>
                  Email
                </a>
                <a className="share-btn share-btn-x" href={xUrl} target="_blank" rel="noopener">
                  <span className="share-btn-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                  </span>
                  X
                </a>
              </div>

              <details className="message-block">
                <summary>
                  <span className="summary-label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                    Need words to send? Here's a starter.
                  </span>
                  <svg className="chev ic-inline" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5 L6 7.5 L9 4.5" /></svg>
                </summary>
                <div className="message-body">
                  <p id="refMessage">
                    "Hi! I've been doing CPD-accredited Mini MBAs on 42courses, taught by the likes of Rory Sutherland and Dan Ariely. Behavioural science, marketing, leadership, AI. Thought you'd enjoy it. Use my code <strong>{refCode}</strong> at checkout for 15% off any course or subscription."
                  </p>
                  <button
                    className={`message-copy-btn${copiedKey === "message" ? " copied" : ""}`}
                    type="button"
                    onClick={() => copy("message", message)}
                  >
                    <CopyIcon />
                    <span className="btn-label">{copiedKey === "message" ? "Copied" : "Copy message"}</span>
                  </button>
                </div>
              </details>
            </div>

            {/* RIGHT: Progress / stamp card */}
            <div className="ref-card">
              <h2>Your stamp card</h2>
              <p className="ref-card-sub">Every friend who buys a course earns you a stamp. Hit five for a course on us, ten for the whole pass.</p>

              <div className="progress-headline">
                <span className="progress-bignum" id="refCount">{achieved}</span>
                <span className="progress-label">referrals so far</span>
              </div>
              <p className="progress-status">{renderStatus()}</p>

              <div className="stamps" role="img" aria-label={`Stamp card: ${achieved} of ${totalSlots} stamps collected`}>
                {stamps.map(({ idx, filled, isCourseReward, isPassReward }) => {
                  const isReward = isCourseReward || isPassReward;
                  const classNames = ["stamp"];
                  if (filled) classNames.push("is-filled");
                  if (isReward) classNames.push("is-reward");

                  let contents: React.ReactNode;
                  if (filled) {
                    contents = <StampMark />;
                  } else if (isCourseReward) {
                    contents = <CourseRewardIcon />;
                  } else if (isPassReward) {
                    contents = <PassRewardIcon />;
                  } else {
                    contents = <span className="stamp-num">{idx + 1}</span>;
                  }

                  return (
                    <div key={`stamp-${idx}`} className={classNames.join(" ")}>
                      {contents}
                    </div>
                  );
                })}
              </div>

              <div className="reward-strip">
                <span className="reward-strip-item">↑ 5th stamp: <strong>course</strong></span>
                <span className="reward-strip-item">↑ 10th stamp: <strong>All Access</strong></span>
              </div>

              <div className="earn-rates">
                <div className="earn-rate">
                  <span className="earn-rate-key">Friend buys a single course</span>
                  <span className="earn-rate-val">+ 1 stamp</span>
                </div>
                <div className="earn-rate">
                  <span className="earn-rate-key">Friend buys an All Access Pass</span>
                  <span className="earn-rate-val">+ 2 stamps</span>
                </div>
              </div>
            </div>

          </div>

          {/* HOW IT WORKS */}
          <div className="how-it-works">
            <div className="section-title">How it works</div>
            <h2 className="section-headline">Three small steps. <em>One nice loop.</em></h2>

            <div className="steps">
              <article className="step">
                <div className="step-num" aria-hidden="true">1</div>
                <h3 className="step-title">Share your code</h3>
                <p className="step-body">Send it to a sharp friend, post it on LinkedIn, drop it in a WhatsApp group. Whatever feels right.</p>
              </article>
              <article className="step">
                <div className="step-num" aria-hidden="true">2</div>
                <h3 className="step-title">They buy a course at 15% off</h3>
                <p className="step-body">Your code applies at checkout. They get the discount automatically. No fine print.</p>
              </article>
              <article className="step">
                <div className="step-num" aria-hidden="true">3</div>
                <h3 className="step-title">A stamp lands on your card</h3>
                <p className="step-body">A pass-buying friend earns you two stamps instead of one. Stamps don't expire.</p>
              </article>
            </div>
          </div>

          {/* REWARD TIERS */}
          <div className="rewards-block">
            <div className="section-title">The rewards</div>
            <h2 className="section-headline">Stack stamps. <em>Pick prizes.</em></h2>

            <div className="reward-tiers">
              <article className="reward-tier">
                <div className="reward-tier-icon" aria-hidden="true">
                  <CourseRewardIcon />
                </div>
                <div className="reward-tier-content">
                  <p className="reward-tier-stamps">5 stamps</p>
                  <h3 className="reward-tier-title">A course, on us.</h3>
                  <p className="reward-tier-sub">Pick any of our certified courses. Behavioural science, AI, copywriting, strategy, leadership. Your call.</p>
                </div>
              </article>

              <article className="reward-tier tier-gold">
                <div className="reward-tier-icon" aria-hidden="true">
                  <PassRewardIcon />
                </div>
                <div className="reward-tier-content">
                  <p className="reward-tier-stamps">10 stamps</p>
                  <h3 className="reward-tier-title">A full All Access Pass.</h3>
                  <p className="reward-tier-sub">A year inside every course, every workshop replay, and everything we ship next. Worth its weight.</p>
                </div>
              </article>
            </div>
          </div>

          {/* FAQ */}
          <div className="faq-block">
            <div className="section-title">Common questions</div>
            <h2 className="section-headline">Worth knowing <em>before you share.</em></h2>

            <div className="faq-list">
              <details className="faq-item">
                <summary>When do I get the stamp?</summary>
                <div className="faq-answer">
                  <p>The moment your friend completes their first purchase with your code. The stamp lands on your card automatically.</p>
                </div>
              </details>

              <details className="faq-item">
                <summary>What if my friend buys more than one course?</summary>
                <div className="faq-answer">
                  <p>You earn the stamp on their first purchase per code. If they go on to buy an All Access Pass later, that's 2 stamps instead of 1, so the bigger commitment is rewarded.</p>
                </div>
              </details>

              <details className="faq-item">
                <summary>Can the same friend earn me multiple stamps?</summary>
                <div className="faq-answer">
                  <p>One referred friend, one stamp. We track by email, so the same person can't double up. (Bots, alas, also banned.)</p>
                </div>
              </details>

              <details className="faq-item">
                <summary>Do stamps expire?</summary>
                <div className="faq-answer">
                  <p>No. Stamps stay on your card forever. Take a year between referrals if you like.</p>
                </div>
              </details>

              <details className="faq-item">
                <summary>How do I redeem when I hit 5 or 10 stamps?</summary>
                <div className="faq-answer">
                  <p>We'll email you the moment you hit a tier with a one-click claim link. Or just drop us a line at <a href="mailto:groundcontrol@42courses.com">groundcontrol@42courses.com</a> and we'll sort it.</p>
                </div>
              </details>

              <details className="faq-item">
                <summary>Can I refer myself?</summary>
                <div className="faq-answer">
                  <p>Nice try. No, your code is tied to your account, so you can't use it on your own purchases.</p>
                </div>
              </details>
            </div>
          </div>

        </div>
      </section>
    </main>
  );
};

export default Referral;
